<template>
  <div class="main-layout">
    <!-- 顶部导航栏 -->
    <el-header class="main-header">
      <div class="header-container">
        <!-- 左侧：Logo和面包屑 -->
        <div class="header-left">
          <div class="logo" @click="goToDashboard">
            <el-icon class="logo-icon">
              <Ticket/>
            </el-icon>
            <span class="logo-text">工单系统</span>
          </div>

          <el-breadcrumb separator="/" class="breadcrumb">
            <el-breadcrumb-item v-for="item in breadcrumb" :key="item.path">
              <router-link v-if="item.path" :to="item.path">
                {{ item.title }}
              </router-link>
              <span v-else>{{ item.title }}</span>
            </el-breadcrumb-item>
          </el-breadcrumb>
        </div>

        <!-- 右侧：用户菜单和通知 -->
        <div class="header-right">
          <!-- 主题切换 -->
          <div class="theme-toggle-container">
            <el-tooltip :content="appStore.isDark ? '切换浅色' : '切换深色'" placement="bottom">
              <el-button
                  type="text"
                  class="theme-toggle-btn"
                  @click="appStore.toggleTheme()"
              >
                <el-icon :size="20">
                  <Moon v-if="appStore.isDark" />
                  <Sunny v-else />
                </el-icon>
              </el-button>
            </el-tooltip>
          </div>
          <!-- 仪表盘按钮 -->
          <div class="dashboard-btn-container">
            <el-tooltip content="仪表盘" placement="bottom">
              <el-button
                  type="text"
                  class="dashboard-btn"
                  @click="goToDashboard"
              >
                <el-icon :size="20">
                  <Odometer/>  <!-- 需要导入Odometer图标 -->
                </el-icon>
              </el-button>
            </el-tooltip>
          </div>
          <!-- 通知中心 -->
          <el-dropdown
              class="notification-dropdown"
              trigger="click"
              placement="bottom-end"
              @command="handleNotificationCommand"
          >
            <div class="notification-trigger">
              <el-badge :value="unreadCount" :max="99" :hidden="unreadCount === 0">
                <el-icon :size="20">
                  <Bell/>
                </el-icon>
              </el-badge>
            </div>

            <template #dropdown>
              <el-dropdown-menu class="notification-menu">
                <el-dropdown-item class="notification-header">
                  <span>通知中心</span>
                  <el-button
                      type="text"
                      size="small"
                      @click="markAllAsRead"
                  >
                    全部已读
                  </el-button>
                </el-dropdown-item>

                <el-dropdown-item
                    v-for="notification in recentNotifications"
                    :key="notification.id"
                    :class="['notification-item', { unread: !notification.read }]"
                    :command="notification"
                >
                  <div class="notification-content">
                    <div class="notification-title">
                      <el-icon
                          v-if="notification.type === 'urgent'"
                          color="#F56C6C"
                      >
                        <Warning/>
                      </el-icon>
                      <strong>{{ notification.title }}</strong>
                    </div>
                    <div class="notification-message">{{ notification.message }}</div>
                    <div class="notification-time">{{ formatTime(notification.created_at) }}</div>
                  </div>
                </el-dropdown-item>

                <el-dropdown-item v-if="notifications.length === 0" class="notification-empty">
                  暂无通知
                </el-dropdown-item>

                <el-dropdown-item v-if="notifications.length > 0" class="notification-footer">
                  <router-link to="/notifications" @click="closeNotifications">
                    查看所有通知
                  </router-link>
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <!-- 用户菜单 -->
          <el-dropdown class="user-dropdown" @command="handleUserCommand">
            <div class="user-info">
              <el-avatar :size="32" :src="userAvatar">
                {{ userInitials }}
              </el-avatar>
              <div class="user-details">
                <span class="user-name">{{ authStore.user?.full_name || authStore.user?.username }}</span>
                <span class="user-role">{{ roleText }}</span>
              </div>
              <el-icon>
                <ArrowDown/>
              </el-icon>
            </div>

            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">
                  <el-icon>
                    <User/>
                  </el-icon>
                  个人资料
                </el-dropdown-item>
                <el-dropdown-item command="settings">
                  <el-icon>
                    <Setting/>
                  </el-icon>
                  设置
                </el-dropdown-item>
                <el-dropdown-item divided command="logout">
                  <el-icon>
                    <SwitchButton/>
                  </el-icon>
                  退出登录
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </el-header>

    <!-- 侧边栏 -->
    <div class="main-container">
      <el-aside class="main-sidebar" :width="sidebarWidth">
        <!-- 侧边栏折叠按钮 -->
        <div class="sidebar-collapse" @click="toggleSidebar">
          <el-icon>
            <component :is="isCollapsed ? 'Expand' : 'Fold'"/>
          </el-icon>
        </div>

        <!-- 导航菜单 -->
        <el-menu
            :default-active="activeMenu"
            :collapse="isCollapsed"
            router
            class="sidebar-menu"
            @select="handleMenuSelect"
        >
          <template v-for="route in permissionRoutes" :key="route.name">
            <!-- 有子菜单且子菜单需要显示 -->
            <el-sub-menu
                v-if="route.children && route.children.filter(c => !c.meta?.hideInMenu).length > 0"
                :index="route.path || route.name"
            >
              <template #title>
                <el-icon v-if="route.meta.icon">
                  <component :is="route.meta.icon"/>
                </el-icon>
                <span>{{ route.meta.title }}</span>
              </template>

              <el-menu-item
                  v-for="child in route.children.filter(c => !c.meta?.hideInMenu)"
                  :key="child.name"
                  :index="child.path || child.name"
                  :route="child"
              >
                <el-icon v-if="child.meta.icon">
                  <component :is="child.meta.icon"/>
                </el-icon>
                <span>{{ child.meta.title }}</span>
              </el-menu-item>
            </el-sub-menu>

            <!-- 无子菜单或子菜单都隐藏 -->
            <el-menu-item
                v-else
                :index="route.path || route.name"
                :route="route"
            >
              <el-icon v-if="route.meta.icon">
                <component :is="route.meta.icon"/>
              </el-icon>
              <span>{{ route.meta.title }}</span>
            </el-menu-item>
          </template>
        </el-menu>

        <!-- 底部信息 -->
        <div class="sidebar-footer">
          <div class="system-info">
            <el-icon>
              <InfoFilled/>
            </el-icon>
            <span v-if="!isCollapsed">系统版本: {{ systemVersion }}</span>
          </div>
        </div>
      </el-aside>

      <!-- 主要内容区域 -->
      <main class="main-content">
        <!-- 标签页 -->
        <div class="page-tabs" v-if="showTabs">
          <el-tabs
              v-model="activeTab"
              type="card"
              closable
              @tab-remove="removeTab"
              @tab-click="clickTab"
          >
            <el-tab-pane
                v-for="tab in tabs"
                :key="tab.name"
                :label="tab.title"
                :name="tab.name"
                :closable="tab.name !== 'Dashboard'"
            >
            </el-tab-pane>
          </el-tabs>
        </div>

        <!-- 页面内容 -->
        <div class="page-content">
          <router-view v-slot="{ Component }">
            <transition name="fade" mode="out-in">
              <component :is="Component"/>
            </transition>
          </router-view>
        </div>
      </main>
    </div>

    <!-- WebSocket连接状态 -->
    <div class="connection-status" :class="connectionStatus">
      <el-icon>
        <component :is="connectionIcon"/>
      </el-icon>
      <span>{{ connectionText }}</span>
    </div>
  </div>
</template>

<script setup>
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue'
import {useRoute, useRouter} from 'vue-router'
import {storeToRefs} from 'pinia'
import {useAuthStore} from '@/stores/auth'
import {useAppStore} from '@/stores/app'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'
// 图标导入
import {
  ArrowDown,
  Bell,
  InfoFilled,
  Moon,
  Odometer,
  Setting,
  Sunny,
  SwitchButton,
  Ticket,
  User,
  Warning
} from '@element-plus/icons-vue'
import {ElMessage, ElNotification} from "element-plus";

dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const appStore = useAppStore()

// 响应式数据
const isCollapsed = ref(false)
const activeTab = ref('')
const tabs = ref([])
const notifications = ref([])
const socket = ref(null)
const connectionState = ref('disconnected')

// 从store中获取数据
const {user} = storeToRefs(authStore)
const {showTabs, systemVersion} = storeToRefs(appStore)

// 计算属性
const sidebarWidth = computed(() => isCollapsed.value ? '64px' : '220px')
const activeMenu = computed(() => route.name)
const breadcrumb = computed(() => {
  const matched = route.matched.filter(item => item.meta?.title)
  return matched.map(item => ({
    title: item.meta.title,
    path: item.path
  }))
})

// 添加事件监听器
const setupEventListeners = () => {
  console.log('设置事件监听器...')

  // 监听登录成功事件
  window.addEventListener('user-login-success', async (event) => {
    console.log('收到登录成功事件:', event.detail)
    console.log('当前用户信息:', authStore.user)
    console.log('认证状态:', authStore.isAuthenticated)

    // 等待一小段时间确保store已更新
    setTimeout(async () => {
      try {
        console.log('开始WebSocket连接流程...')
        await initWebSocketConnection()
      } catch (error) {
        console.error('WebSocket连接失败:', error)
      }
    }, 100)
  })

  // 监听应用初始化完成事件
  window.addEventListener('app-initialized', async (event) => {
    console.log('收到应用初始化完成事件:', event.detail)

    if (event.detail?.user && authStore.isAuthenticated) {
      console.log('应用已初始化且用户已登录，尝试连接WebSocket')
      setTimeout(async () => {
        try {
          await initWebSocketConnection()
        } catch (error) {
          console.error('初始化后WebSocket连接失败:', error)
        }
      }, 200)
    }
  })
}

const permissionRoutes = computed(() => {
  const mainRoutes = router.options.routes.find(r => r.path === '/')
  if (!mainRoutes?.children) return []

  console.log('所有路由:', mainRoutes.children) // 调试

  return mainRoutes.children.filter(route => {
    // 检查是否需要超级管理员权限
    if (route.meta?.requiresSuperAdmin && !isSuperAdmin.value) {
      return false
    }

    // 检查是否需要管理员权限
    if (route.meta?.requiresAdmin && !authStore.isAdmin) {
      return false
    }

    // 检查具体权限
    if (route.meta?.requiresPermission) {
      const hasPermission = route.meta.requiresPermission.some(permission =>
          authStore.permissions?.includes(permission)
      )
      if (!hasPermission) return false
    }

    return true
  }).map(route => {
    // 处理子路由的权限过滤
    if (route.children && route.children.length > 0) {
      const filteredChildren = route.children.filter(child => {
        // 检查子路由是否需要超级管理员权限
        console.log('检查路由:', route.name, route.meta) // 调试
        if (child.meta?.requiresSuperAdmin && !isSuperAdmin.value) {
          console.log('需要超级管理员权限，跳过:', route.name) // 调试
          return false
        }

        // 检查子路由是否需要管理员权限
        if (child.meta?.requiresAdmin && !authStore.isAdmin) {
          return false
        }

        // 检查子路由的具体权限
        if (child.meta?.requiresPermission) {
          const hasPermission = child.meta.requiresPermission.some(permission =>
              authStore.permissions?.includes(permission)
          )
          if (!hasPermission) return false
        }

        return !child.meta?.hideInMenu
      })

      return {
        ...route,
        children: filteredChildren
      }
    }
    return route
  })
})

const isSuperAdmin = computed(() => {
  const user = authStore.user
  // 允许 super_admin 和 admin 角色
  return user?.role === 'super_admin' || user?.role === 'admin' || user?.is_super_admin
})

const userAvatar = computed(() => user.value?.avatar_url || '')
const userInitials = computed(() => authStore.userInitials)
const roleText = computed(() => {
  const roles = {
    admin: '管理员',
    manager: '经理',
    user: '用户'
  }
  return roles[user.value?.role] || '用户'
})

const recentNotifications = computed(() => {
  return notifications.value.slice(0, 5)
})

const unreadCount = computed(() => {
  return notifications.value.filter(n => !n.read).length
})

const connectionStatus = computed(() => {
  return {
    connected: 'connected',
    disconnected: 'disconnected',
    connecting: 'connecting'
  }[connectionState.value]
})

const connectionIcon = computed(() => {
  const icons = {
    connected: 'SuccessFilled',
    disconnected: 'CircleCloseFilled',
    connecting: 'Loading'
  }
  return icons[connectionState.value] || 'CircleCloseFilled'
})

const connectionText = computed(() => {
  const texts = {
    connected: '已连接',
    disconnected: '未连接',
    connecting: '连接中...'
  }
  return texts[connectionState.value] || '未连接'
})

// 方法
const goToDashboard = () => {
  router.push('/dashboard')
}

const toggleSidebar = () => {
  isCollapsed.value = !isCollapsed.value
  appStore.setSidebarCollapsed(isCollapsed.value)
}

const handleUserCommand = (command) => {
  switch (command) {
    case 'profile':
      router.push('/profile')
      break
    case 'settings':
      router.push('/settings/general')
      break
    case 'logout':
      authStore.logout()
      break
  }
}

const handleNotificationCommand = (notification) => {
  if (notification.workorder_id) {
    router.push(`/workorders/${notification.workorder_id}`)
  }
  markAsRead(notification.id)
}

const handleMenuSelect = (index, indexPath) => {
  // 添加标签页
  const route = router.options.routes
      .flatMap(r => r.children || [])
      .find(r => r.name === index)

  if (route && route.path !== '/' && !route.meta?.hideInTabs) {
    addTab({
      name: route.name,
      title: route.meta?.title || route.name,
      path: route.path
    })
  }
}

const addTab = (tab) => {
  const exists = tabs.value.find(t => t.name === tab.name)
  if (!exists) {
    tabs.value.push(tab)
  }
  activeTab.value = tab.name
}

const removeTab = (targetName) => {
  // 如果是仪表板，不允许关闭
  if (targetName === 'Dashboard') {
    return
  }

  const tabsArray = tabs.value
  let activeName = activeTab.value

  if (activeName === targetName) {
    tabsArray.forEach((tab, index) => {
      if (tab.name === targetName) {
        const nextTab = tabsArray[index + 1] || tabsArray[index - 1]
        if (nextTab) {
          activeName = nextTab.name
        }
      }
    })
  }

  tabs.value = tabsArray.filter(tab => tab.name !== targetName)
  activeTab.value = activeName

  if (activeName) {
    const tab = tabs.value.find(t => t.name === activeName)
    if (tab) {
      router.push(tab.path)
    }
  }
}

const clickTab = (tab) => {
  const targetTab = tabs.value.find(t => t.name === tab.paneName)
  if (targetTab) {
    router.push(targetTab.path)
  }
}

const formatTime = (time) => {
  return dayjs(time).fromNow()
}

const markAsRead = (notificationId) => {
  const notification = notifications.value.find(n => n.id === notificationId)
  if (notification) {
    notification.read = true
  }
}

const markAllAsRead = () => {
  notifications.value.forEach(notification => {
    notification.read = true
  })
}

const closeNotifications = () => {
  // 关闭通知下拉框的逻辑由Element Plus自动处理
}

// WebSocket连接
const connectWebSocket = async () => {
  // 检查是否已连接或正在连接
  if (socket.value) {
    const state = socket.value.readyState
    if (state === WebSocket.OPEN) {
      console.log('WebSocket已连接，跳过')
      return
    }
    if (state === WebSocket.CONNECTING) {
      console.log('WebSocket正在连接中，等待...')
      return
    }
  }

  // 首先确保用户信息完整
  try {
    console.log('连接WebSocket前，确保用户信息完整...')
    await authStore.ensureUserInfoComplete()
  } catch (error) {
    console.error('获取完整用户信息失败:', error)
    return
  }

  // 检查token和用户ID
  if (!authStore.accessToken || !authStore.user?.id) {
    console.log('未登录或用户信息不完整，跳过WebSocket连接')
    return
  }

  connectionState.value = 'connecting'
  console.log('正在连接WebSocket...', {
    userId: authStore.user.id,
    username: authStore.user.username
  })

  try {
    const wsUrl = process.env.VUE_APP_SOCKET_URL
        ? `${process.env.VUE_APP_SOCKET_URL.replace('https', 'wss')}/wsw/${authStore.user.id}`
        : `wss://${window.location.host}/wsw/${authStore.user.id}`

    console.log('WebSocket连接URL:', wsUrl)

    // 断开现有连接（如果存在）
    if (socket.value) {
      disconnectWebSocket()
    }

    socket.value = new WebSocket(wsUrl)

    // WebSocket事件监听
    socket.value.onopen = () => {
      console.log('WebSocket onopen 触发')

      // 检查连接状态
      if (socket.value.readyState !== WebSocket.OPEN) {
        console.warn('WebSocket在onopen中状态不是OPEN:', socket.value.readyState)
        return
      }

      connectionState.value = 'connected'
      console.log('WebSocket连接成功，状态:', socket.value.readyState)

      // 等待一小段时间确保连接稳定
      setTimeout(() => {
        if (socket.value && socket.value.readyState === WebSocket.OPEN) {
          try {
            // 加入用户个人房间
            socket.value.send(JSON.stringify({
              type: 'join',
              room: `user_${authStore.user.id}`,
              userId: authStore.user.id,
              username: authStore.user.username,
              timestamp: new Date().toISOString()
            }))
            console.log('发送加入房间消息成功')
          } catch (sendError) {
            console.error('发送加入房间消息失败:', sendError)
          }
        } else {
          console.warn('WebSocket未就绪，无法发送消息，状态:', socket.value?.readyState)
        }
      }, 2000)
    }

    socket.value.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        console.log('收到WebSocket消息:', data)

        switch (data.type) {
          case 'notification':
            // 处理通知消息
            addToNotificationList({
              id: Date.now(),
              title: data.title || '新通知',
              message: data.content || '',
              type: data.notification_type || 'info',
              created_at: data.timestamp || new Date().toISOString(),
              read: false,
              workorder_id: data.data?.workorder_id,
              from_user: data.from_user
            })

            // 显示桌面通知
            if (data.notification_type === 'urgent') {
              showNotification({
                title: data.title || '紧急通知',
                message: data.content || '',
                type: 'warning',
                duration: 6000
              })
            } else {
              showNotification({
                title: data.title || '新通知',
                message: data.content || '',
                type: 'info'
              })
            }
            break

          case 'new_message':
            // 处理新消息通知（如果不在工单详情页）
            if (route.path !== `/workorders/${data.message?.work_order_id}`) {
              showNotification({
                title: '新消息',
                message: `${data.message?.sender?.username || '用户'}: ${data.message?.content || '发来一条新消息'}`,
                type: 'info'
              })
            }
            break

          case 'heartbeat_response':
            // 心跳响应
            console.log('心跳响应:', data.timestamp)
            break

          case 'acknowledgment':
            // 消息确认回执
            console.log('消息处理确认:', data.original_type)
            break

          case 'error':
            // 错误消息
            console.error('WebSocket错误:', data.error, data.code)
            ElMessage.error(`WebSocket错误: ${data.error}`)
            break

          case 'room_users':
            // 房间用户列表
            console.log('房间用户列表:', data.users)
            break
        }
      } catch (error) {
        console.error('解析WebSocket消息失败:', error)
      }
    }

    socket.value.onclose = (event) => {
      connectionState.value = 'disconnected'
      console.log('WebSocket断开连接:', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      })

      // 如果不是正常关闭，5秒后重试
      if (event.code !== 1000 && authStore.isAuthenticated) {
        console.log('连接异常断开，5秒后重试...')
        setTimeout(() => {
          if (authStore.isAuthenticated) {
            console.log('尝试重新连接WebSocket...')
            connectWebSocket()
          }
        }, 5000)
      }
    }

    socket.value.onerror = (error) => {
      console.error('WebSocket连接错误:', error)
      connectionState.value = 'disconnected'
    }

  } catch (error) {
    console.error('WebSocket连接异常:', error)
    connectionState.value = 'disconnected'
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messageList.value) {
      messageList.value.scrollTop = messageList.value.scrollHeight
    }
  })
}

const showNotification = (options) => {
  const {title, message, type = 'info', duration = 4500} = options

  // 显示 Element Plus 通知
  ElNotification({
    title,
    message,
    type,
    duration,
    position: 'bottom-right'
  })

  // 同时添加到通知列表（如果需要在通知中心显示）
  addToNotificationList({
    id: Date.now(),
    title,
    message,
    type,
    created_at: new Date().toISOString(),
    read: false
  })
}

// 添加到通知列表的方法
const addToNotificationList = (notification) => {
  notifications.value.unshift(notification)

  // 限制通知数量，最多保留50条
  if (notifications.value.length > 50) {
    notifications.value = notifications.value.slice(0, 50)
  }
}

const disconnectWebSocket = () => {
  if (socket.value) {
    // 检查状态
    const state = socket.value.readyState
    console.log('断开WebSocket连接，当前状态:', state)

    if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) {
      try {
        // 发送关闭消息（可选）
        if (state === WebSocket.OPEN) {
          socket.value.send(JSON.stringify({
            type: 'leave',
            userId: authStore.user?.id,
            timestamp: new Date().toISOString()
          }))
        }

        // 关闭连接
        socket.value.close(1000, '正常关闭')
      } catch (error) {
        console.error('关闭WebSocket时出错:', error)
      }
    }
    socket.value = null
  }
  connectionState.value = 'disconnected'
}

// 生命周期
onMounted(async () => {
  console.log('MainLayout mounted, 当前认证状态:', authStore.isAuthenticated)
  if (authStore.user && (!authStore.user.id || !authStore.user.username)) {
    console.warn('用户数据不完整，尝试修复...')
    authStore.fixUserData()
  }

  // 从localStorage恢复侧边栏状态
  const savedState = localStorage.getItem('sidebar_collapsed')
  if (savedState !== null) {
    isCollapsed.value = JSON.parse(savedState)
  }

  // 立即设置事件监听器
  setupEventListeners()

  // 检查是否已经登录（从其他标签页返回等场景）
  if (authStore.isAuthenticated) {
    console.log('页面加载时已认证，尝试连接WebSocket')
    await initWebSocketConnection()
  }

  // 初始化标签页
  addTab({
    name: route.name,
    title: route.meta?.title || route.name,
    path: route.path
  })
})

// 创建独立的初始化函数
const initWebSocketConnection = async () => {
  console.log('初始化WebSocket连接...')
  console.log('当前认证状态:', {
    isAuthenticated: authStore.isAuthenticated,
    hasToken: !!authStore.accessToken,
    hasUser: !!authStore.user,
    userId: authStore.user?.id,
    username: authStore.user?.username
  })

  // 1. 确保已认证
  if (!authStore.isAuthenticated) {
    console.log('未认证，跳过WebSocket连接')
    return
  }

  // 2. 确保用户信息完整
  if (!authStore.user || !authStore.user.id) {
    console.log('用户信息不完整，尝试获取...')
    try {
      await authStore.ensureUserInfoComplete()

      if (!authStore.user || !authStore.user.id) {
        console.error('用户信息仍然不完整，无法连接WebSocket')
        return
      }
    } catch (error) {
      console.error('获取完整用户信息失败:', error)
      return
    }
  }

  // 3. 检查token和用户ID
  if (!authStore.accessToken || !authStore.user?.id) {
    console.log('认证信息不完整，跳过WebSocket连接')
    return
  }

  console.log('所有条件满足，开始连接WebSocket...')

  // 确保不会重复连接
  if (socket.value && socket.value.readyState === WebSocket.CONNECTING) {
    console.log('WebSocket已经在连接中，跳过')
    return
  }

  await connectWebSocket()
}

onMounted(async () => {
  console.log('MainLayout mounted, 当前认证状态:', {
    isAuthenticated: authStore.isAuthenticated,
    hasUser: !!authStore.user,
    userId: authStore.user?.id,
    username: authStore.user?.username
  })

  // 从localStorage恢复侧边栏状态
  const savedState = localStorage.getItem('sidebar_collapsed')
  if (savedState !== null) {
    isCollapsed.value = JSON.parse(savedState)
  }

  // 立即设置事件监听器
  setupEventListeners()

  // 检查是否已经登录
  if (authStore.isAuthenticated) {
    console.log('页面加载时已认证，检查用户信息...')

    if (authStore.user && authStore.user.id) {
      console.log('用户信息完整，等待1秒后尝试连接WebSocket')
      // 等待1秒，确保所有初始化完成
      setTimeout(async () => {
        await initWebSocketConnection()
      }, 1000)
    } else {
      console.log('用户信息不完整，等待应用初始化完成')
      // 等待应用初始化事件
    }
  } else {
    console.log('用户未登录，不连接WebSocket')
  }

  // 初始化标签页
  addTab({
    name: route.name,
    title: route.meta?.title || route.name,
    path: route.path
  })
})

onUnmounted(() => {
  console.log('MainLayout unmounted')

  // 确保WebSocket断开连接
  disconnectWebSocket()

  // 清理事件监听器
  window.removeEventListener('user-login-success', () => {
  })
  window.removeEventListener('app-initialized', () => {
  })

  // 清理定时器（如果有的话）
  if (window.socketRetryTimer) {
    clearTimeout(window.socketRetryTimer)
    delete window.socketRetryTimer
  }
})

// 监听路由变化
watch(() => route.fullPath, () => {
  if (route.name && !route.meta?.hideInTabs) {
    addTab({
      name: route.name,
      title: route.meta?.title || route.name,
      path: route.path
    })
  }
})

// 监听认证状态
watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth) {
    connectWebSocket()
  } else {
    disconnectWebSocket()
  }
})
</script>

<style scoped lang="scss">
.main-layout {
  height: 100vh;
  display: flex;
  flex-direction: column;

  .main-header {
    background: var(--gradient-primary);
    color: white;
    height: 60px;
    padding: 0;
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
    z-index: 1000;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    }

    .header-container {
      height: 100%;
      max-width: 100%;
      margin: 0 auto;
      padding: 0 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .header-left {
        display: flex;
        align-items: center;
        gap: 20px;

        .logo {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          transition: all var(--transition-base);

          &:hover {
            transform: scale(1.03);
            filter: brightness(1.1);
          }

          .logo-icon {
            font-size: 24px;
            animation: float 3s ease-in-out infinite;
          }

          .logo-text {
            font-size: 20px;
            font-weight: 600;
            color: white;
            letter-spacing: 1px;
          }
        }

        .breadcrumb {
          :deep(.el-breadcrumb__inner) {
            color: rgba(255, 255, 255, 0.9);

            a {
              color: rgba(255, 255, 255, 0.9);

              &:hover {
                color: white;
              }
            }
          }

          :deep(.el-breadcrumb__separator) {
            color: rgba(255, 255, 255, 0.6);
          }
        }
      }

      .header-right {
        display: flex;
        align-items: center;
        gap: 12px;

        .theme-toggle-container {
          .theme-toggle-btn {
            color: white;
            padding: 8px;
            border-radius: 8px;
            transition: all var(--transition-base);

            &:hover {
              background: rgba(255, 255, 255, 0.15);
              transform: rotate(15deg) scale(1.1);
            }

            .el-icon {
              transition: transform var(--transition-base);
            }
          }
        }

        .dashboard-btn-container {
          margin-right: 4px;

          .dashboard-btn {
            &.active {
              background: rgba(255, 255, 255, 0.2);
              color: #ffd04b;
              box-shadow: 0 0 0 1px rgba(255, 208, 75, 0.3);
            }

            &:hover {
              background: rgba(255, 255, 255, 0.1);
            }

            .el-icon {
              vertical-align: middle;
            }
          }
        }

        .notification-dropdown {
          .notification-trigger {
            position: relative;
            cursor: pointer;
            padding: 8px;
            border-radius: 8px;
            color: white;
            transition: all var(--transition-base);
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            height: 36px;

            &:hover {
              background: rgba(255, 255, 255, 0.15);
              transform: scale(1.05);
            }

            .el-icon {
              font-size: 20px;
            }

            :deep(.el-badge__content) {
              border: 2px solid #667eea;
              top: -4px;
              right: -4px;
              background: #ff4757;
              color: white;
              font-weight: bold;
            }
          }

          .notification-menu {
            width: 320px;
            max-height: 400px;
            overflow-y: auto;

            .notification-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 10px 12px;
              font-weight: 600;
              border-bottom: 1px solid var(--border-lighter);
            }

            .notification-item {
              padding: 12px;
              border-bottom: 1px solid var(--border-lighter);
              transition: background var(--transition-fast);

              &.unread {
                background: var(--bg-hover);
              }

              &:hover {
                background: var(--bg-hover);
              }

              .notification-content {
                .notification-title {
                  display: flex;
                  align-items: center;
                  gap: 6px;
                  margin-bottom: 4px;

                  strong {
                    flex: 1;
                    color: var(--text-primary);
                  }
                }

                .notification-message {
                  color: var(--text-secondary);
                  font-size: 13px;
                  margin-bottom: 4px;
                  line-height: 1.4;
                }

                .notification-time {
                  color: var(--text-placeholder);
                  font-size: 12px;
                }
              }
            }

            .notification-empty {
              color: var(--text-secondary);
              text-align: center;
              padding: 20px;
            }

            .notification-footer {
              text-align: center;
              padding: 10px;

              a {
                color: var(--app-primary);
                text-decoration: none;

                &:hover {
                  text-decoration: underline;
                }
              }
            }
          }
        }

        .user-dropdown {
          .user-info {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 8px 12px;
            border-radius: 8px;
            cursor: pointer;
            transition: all var(--transition-base);

            &:hover {
              background: rgba(255, 255, 255, 0.12);
              transform: translateY(-1px);
            }

            .user-details {
              display: flex;
              flex-direction: column;

              .user-name {
                font-weight: 500;
                color: white;
              }

              .user-role {
                font-size: 12px;
                color: rgba(255, 255, 255, 0.7);
              }
            }
          }
        }
      }
    }
  }

  .main-container {
    flex: 1;
    display: flex;
    overflow: hidden;

    .main-sidebar {
      background: var(--bg-sidebar);
      border-right: 1px solid var(--border-lighter);
      display: flex;
      flex-direction: column;
      transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1), background var(--transition-base);
      box-shadow: 2px 0 12px rgba(0, 0, 0, 0.06);
      position: relative;
      z-index: 100;

      .sidebar-collapse {
        height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        border-bottom: 1px solid var(--border-lighter);
        color: var(--text-secondary);
        transition: all var(--transition-base);

        &:hover {
          background: var(--bg-hover);
          color: var(--app-primary);
        }

        .el-icon {
          font-size: 18px;
          transition: transform var(--transition-base);
        }

        &:hover .el-icon {
          transform: scale(1.2);
        }
      }

      .sidebar-menu {
        flex: 1;
        border-right: none;

        :deep(.el-menu-item),
        :deep(.el-sub-menu__title) {
          height: 48px;
          line-height: 48px;
          transition: all var(--transition-fast);

          .el-icon {
            margin-right: 8px;
          }
        }

        :deep(.el-menu-item.is-active) {
          background: var(--bg-hover);
          color: var(--app-primary);

          &:before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 3px;
            background: var(--app-primary);
            border-radius: 0 2px 2px 0;
          }
        }
      }

      .sidebar-footer {
        border-top: 1px solid var(--border-lighter);
        padding: 12px;

        .system-info {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-secondary);
          font-size: 12px;

          .el-icon {
            font-size: 14px;
          }
        }
      }
    }

    .main-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      background: var(--bg-page);
      transition: background var(--transition-base);

      .page-tabs {
        background: var(--bg-card);
        border-bottom: 1px solid var(--border-lighter);
        transition: background var(--transition-base);

        :deep(.el-tabs__header) {
          margin: 0;

          .el-tabs__nav-wrap {
            padding: 0 20px;
          }
        }
      }

      .page-content {
        flex: 1;
        padding: 20px;
        overflow-y: auto;

        .fade-enter-active,
        .fade-leave-active {
          transition: opacity 0.3s ease, transform 0.3s ease;
        }

        .fade-enter-from {
          opacity: 0;
          transform: translateY(8px);
        }

        .fade-leave-to {
          opacity: 0;
          transform: translateY(-8px);
        }
      }
    }
  }

  .connection-status {
    position: fixed;
    bottom: 20px;
    right: 20px;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
    z-index: 1000;
    backdrop-filter: blur(8px);
    box-shadow: var(--shadow-md);
    transition: all var(--transition-base);

    &.connected {
      background: rgba(103, 194, 58, 0.15);
      color: var(--app-success);
      border: 1px solid rgba(103, 194, 58, 0.3);
    }

    &.disconnected {
      background: rgba(245, 108, 108, 0.15);
      color: var(--app-danger);
      border: 1px solid rgba(245, 108, 108, 0.3);
    }

    &.connecting {
      background: rgba(230, 162, 60, 0.15);
      color: var(--app-warning);
      border: 1px solid rgba(230, 162, 60, 0.3);
    }
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}
</style>